import React from 'react'
import { HeadingPages } from '../../../../components/HeadingPages'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Box, Center, Flex, Spinner, Text, useToast, Divider, AbsoluteCenter, Image, SimpleGrid, Grid, Tooltip } from '@chakra-ui/react'
import { api } from '../../../../services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { user } from '../../../../helpers/user'
import { TextEditor } from '../../../../components/TextEditor'
import { ListaPerguntasRespostas } from './ListaPerguntasRespostas'
import { ListaTopicos } from './ListaTopicos'
import { Button } from '../../../../components/Button'
import { SecaoPrincial } from './SecaoPrincipal'
import { SubSecao } from './SubSecao'
import { ArrowLeftIcon } from '@chakra-ui/icons'
import { ListaDuvidas } from './ListaDuvidas'

const schemaCidade = z.object({
  id: z.string().optional(),
  secao_principal: z.object({
    titulo: z.string().optional(),
    descricao: z.string().optional(),
  }),
  descricao: z.string().optional(),
  sub_secao: z.object({
    titulo: z.string().optional(),
    descricao: z.string().optional(),
  }),
  descricao_cidade: z.string().optional(),
  duvidas: z.object({
    titulo: z.string().optional(),
    descricao: z.string().optional(),
    topicos: z.array(
      z.object({
        id_topico: z.string().optional(),
        pergunta: z.string().optional(),
        resposta: z.string().optional(),
      })
    ),
  }),
  faq: z.array(
    z.object({
      id_pergunta: z.string().optional(),
      pergunta: z.string().optional(),
      resposta: z.string().optional(),
    })
  ),
  sobre: z.object({
    titulo: z.string().optional(),
    descricao: z.string().optional(),
  }),
  titulo_imagem: z.string().optional(),
})

async function gerarGpt({ cidade, campo }) {
  {
    /*
    Campos que geram:
    - descricao 
    - secao_principal
    - sub_secao
    - duvidas 
    - faq (no caso ele irá gerar todas as respostas e manter as perguntas que deixamos)
    - sobre
    - topicos
  */
  }

  const response = await api.get('cidades-home/gerar-conteudo-gpt-cidade', {
    params: {
      campo: campo,
      nome_cidade: cidade,
    },
  })

  return response.data
}

async function publicarCidade({ id, nomeUsuario }) {
  const response = await api.put(`/cidades-home/aprovar-conteudo`, { id: id, aprovar: 1, nome_usuario: nomeUsuario })
  return response.data
}

async function updateCidade({ data }) {
  const response = await api.put(`/cidades-home/editar-cidade`, data)
  return response.data
}

async function getlAllCidades({ slug }) {
  const response = await api.get(`/cidades-home/listar-crm/${slug}`, {
    params: {
      aprovados: false,
    },
  })
  return response.data
}

export const Cidade = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const methods = useForm({
    resolver: zodResolver(schemaCidade),
  })
  const toast = useToast({
    position: 'top-right',
    duration: 5000,
  })

  const queryClient = useQueryClient()

  const { control, handleSubmit, setValue, formState } = methods

  const { mutateAsync: gerarGptAsync, isLoading: isLoadingGerarGpt } = useMutation({
    mutationFn: gerarGpt,
    onSuccess: (data) => {
      const { campo, conteudo } = data

      toast({
        title: 'Conteúdo gerado com sucesso!',
        description: `Campo: ${campo}`,
        status: 'success',
      })

      if (campo === 'descricao_cidade') {
        setValue('descricao_cidade', conteudo)
        return
      }

      if (campo === 'descricao') {
        setValue('descricao', conteudo)
        return
      }

      if (campo === 'topicos') {
        setValue('duvidas.topicos', conteudo)
        return
      }

      if (campo === 'duvidas') {
        setValue('duvidas.descricao', conteudo)
        return
      }

      if (campo === 'faq') {
        setValue('faq', conteudo)
        return
      }

      if (campo === 'secao_principal') {
        setValue('secao_principal.descricao', conteudo)
        return
      }

      if (campo === 'sub_secao') {
        setValue('sub_secao.descricao', conteudo)
        return
      }

      if (campo === 'sobre') {
        setValue('sobre.descricao', conteudo)
        return
      }
    },
    onError: () => {
      toast({
        title: 'Erro ao gerar conteúdo',
        status: 'error',
      })
    },
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['cidades', { slug }],
    queryFn: () => getlAllCidades({ slug }),
    refetchOnWindowFocus: false,
    staleTime: 5000,
    onSuccess: (data) => {
      console.log('data', data)
      setValue('id', data.id_cidade)
      setValue('secao_principal.titulo', data.secao_principal.titulo)
      setValue('secao_principal.descricao', data.secao_principal.descricao)
      setValue('descricao', data.descricao)
      setValue('sub_secao.titulo', data.sub_secao.titulo)
      setValue('sub_secao.descricao', data.sub_secao.descricao)
      setValue('descricao_cidade', data.descricao_cidade)
      setValue('duvidas.titulo', data.duvidas.titulo)
      setValue('duvidas.descricao', data.duvidas.descricao)
      setValue('duvidas.topicos', data.duvidas.topicos)
      setValue('faq', data.faq)
      setValue('sobre.titulo', data.sobre.titulo)
      setValue('sobre.descricao', data.sobre.descricao)
      setValue('titulo_imagem', data.titulo_imagem)
    },
  })

  const {
    mutateAsync,
    isLoading: isLoadingUpdateArticle,
    isSuccess,
  } = useMutation({
    mutationFn: updateCidade,
    onSuccess: () => {
      toast({
        title: 'Cidade salva com sucesso!',
        status: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao salvar cidade',
        status: 'error',
      })
    },
  })

  const { mutateAsync: publicarCidadeAsync, isLoading: isLoadingPublicarCidade } = useMutation({
    mutationFn: publicarCidade,
    onSuccess: () => {
      queryClient.invalidateQueries('cidades')
      navigate('/adm/cidades')

      toast({
        title: 'Cidade publicada com sucesso!',
        status: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao publicar cidade',
        status: 'error',
      })
    },
  })

  async function handlePublicarCidade() {
    const params = {
      id: data.id_cidade,
      nomeUsuario: user.nome_usuario,
    }

    await publicarCidadeAsync(params)
  }
  console.log(formState.errors)

  async function handleUpdateCidade(data) {
    await mutateAsync({ data })
  }

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    )
  }

  if (error) {
    return (
      <Box w="100%" mt="10px">
        <HeadingPages name="Erro" />
        <Flex justify="center" align="center" flexDir="column" gap={5}>
          <Box>
            <Box w="100%" p="20px" borderRadius="sm" bg="red.50">
              <Text fontSize="xl" fontWeight="bold" color="red.500" textAlign="center" textTransform="uppercase">
                Erro ao buscar cidade
              </Text>
            </Box>
          </Box>

          <Link to="/adm/artigos">
            <Text fontSize="lg" color="blue.500" fontWeight="bold">
              Voltar
            </Text>
          </Link>
        </Flex>
      </Box>
    )
  }

  if (!slug) navigate('/adm/artigos')

  return (
    <Box bg="white">
      <HeadingPages name={`Cidade - ${data?.nome_cidade}`} />

      <FormProvider {...methods}>
        <Box as="form" w="full" experimental_spaceY={20} bgColor="white" onSubmit={handleSubmit(handleUpdateCidade)}>
          <input type="hidden" {...control.register('id')} />
          <Box w="full" maxW="container.xl" mx="auto" experimental_spaceY={20} bgColor="white">
            <Grid gridTemplateColumns={'1fr 500px'}>
              <Flex flexDir={'column'} gap={5}>
                <Text lineHeight={'9'} fontSize="4xl" fontWeight="bold">
                  Brindes Personalizados <br /> em {data?.nome_cidade}: <br /> Quais escolher e onde encontrar?
                </Text>
                <Flex flexDir={'column'} gap={5}>
                  <Button
                    onClick={() => gerarGptAsync({ cidade: data.nome_cidade, campo: 'descricao' })}
                    colorScheme="blue"
                    size="lg"
                    bgColor="blue.500"
                    isLoading={isLoadingGerarGpt}
                    mr="auto"
                  >
                    Gerar Descrição
                  </Button>

                  <Text fontSize="23px" fontWeight="bold">
                    Descrição
                  </Text>
                  <Controller
                    name={`descricao`}
                    control={control}
                    render={({ field }) => <TextEditor height="80px" name={`descricao`} {...field} placeholder="Resposta" />}
                  />
                </Flex>
              </Flex>

              <Image
                objectFit="cover"
                src="https://imgproductioncrm.s3.us-east-2.amazonaws.com/banner-institucional%20desk.jpg1721084579.9595"
                w="full"
                h="full"
              />
            </Grid>

            <Grid gridTemplateColumns={'350px 1fr'} gap={5}>
              <Flex flexDir="column" gap={2} borderRight="1px" borderColor="gray.300" borderStyle="solid">
                {Array.from({ length: 15 }).map((_, index) => (
                  <Text key={index}>Link do menu {index}</Text>
                ))}
              </Flex>

              <Flex flexDir={'column'}>
                <Box position="relative" paddingY="5">
                  <Divider orientation="horizontal" borderColor="gray.300" borderWidth="1px" borderStyle="solid" />

                  <AbsoluteCenter bg="white" px="4">
                    Seção Principal
                  </AbsoluteCenter>
                </Box>

                <Flex flexDir={'column'} gap={20}>
                  <Button
                    onClick={() => gerarGptAsync({ cidade: data.nome_cidade, campo: 'secao_principal' })}
                    colorScheme="blue"
                    size="lg"
                    bgColor="blue.500"
                    isLoading={isLoadingGerarGpt}
                    mb="-10"
                    mr="auto"
                  >
                    Gerar Seção Principal (Descrição)
                  </Button>

                  <SecaoPrincial.Titulo />

                  <SecaoPrincial.Descricao />

                  <SecaoPrincial.Categoria />

                  <Flex flexDir={'column'} gap={5}>
                    <Box position="relative">
                      <Divider orientation="horizontal" borderColor="gray.300" borderWidth="1px" borderStyle="solid" />

                      <AbsoluteCenter bg="white" px="4">
                        Sub Seção
                      </AbsoluteCenter>
                    </Box>

                    <Button
                      onClick={() => gerarGptAsync({ cidade: data.nome_cidade, campo: 'sub_secao' })}
                      colorScheme="blue"
                      size="lg"
                      bgColor="blue.500"
                      isLoading={isLoadingGerarGpt}
                      mr="auto"
                    >
                      Gerar Sub Seção (Descrição)
                    </Button>

                    <SubSecao.Titulo />
                  </Flex>

                  <SubSecao.Descricao />

                  <SubSecao.Produtos />
                </Flex>
              </Flex>
            </Grid>

            <Flex flexDir={'column'} gap={5}>
              <Text fontSize="23px" fontWeight="bold" textTransform="uppercase">
                BENEFÍCIOS EXCLUSIVOS DA INNOVATION BRINDES: COMO EMPRESAS EM {data?.nome_cidade} PODEM APROVEITAR?
              </Text>
              <Box w="100%" height="370px" bg="gray.200" />
            </Flex>

            <Flex flexDir={'column'} gap={5}>
              <Text fontSize="23px" fontWeight="bold" textTransform="uppercase">
                DIFERENCIAIS DA INNOVATION BRINDES: QUAIS VANTAGENS SE DESTACAM NO MERCADO DE BRINDES EM {data?.nome_cidade}?
              </Text>
              <Box w="100%" position="relative" height="520px" mt="10">
                <Box position="absolute" display="flex" gap={2} alignItems="center" justifyContent="center" zIndex={2} w="100%">
                  <Box bg="white" w="466px" h="500px" border="1px solid #cfcfcf" transform={'translateY(-30px)'} />
                  <Box bg="white" w="322px" h="500px" border="1px solid #cfcfcf" transform={'translateY(-30px)'} />
                  <Box bg="white" w="322px" h="500px" border="1px solid #cfcfcf" transform={'translateY(-30px)'} />
                </Box>
                <Box w="100%" height="520px" bg="gray.200" position="absolute" />
              </Box>
            </Flex>

            <SubSecao.Produtos quantidade={9} />

            <Flex flexDir={'column'} gap={5}>
              <Box position="relative" paddingY="5">
                <Divider orientation="horizontal" borderColor="gray.300" borderWidth="1px" borderStyle="solid" />

                <AbsoluteCenter bg="white" px="4">
                  Cidade
                </AbsoluteCenter>
              </Box>

              <Flex flexDir={'column'} gap={5}>
                <Button
                  onClick={() => gerarGptAsync({ cidade: data.nome_cidade, campo: 'descricao_cidade' })}
                  colorScheme="blue"
                  size="lg"
                  bgColor="blue.500"
                  isLoading={isLoadingGerarGpt}
                  mr="auto"
                >
                  Gerar Descrição da Cidade
                </Button>
                <Flex flexDir={'column'} gap={5} mb={20}>
                  <Text fontSize="23px" fontWeight="bold">
                    Título da cidade
                  </Text>
                  <Controller
                    name={`titulo_imagem`}
                    control={control}
                    render={({ field }) => <TextEditor height="50px" name={`titulo_imagem`} {...field} placeholder="Resposta" />}
                  />
                </Flex>

                <Flex flexDir={'column'} gap={5} mb={20}>
                  <Text fontSize="23px" fontWeight="bold">
                    Descrição da cidade
                  </Text>
                  <Controller
                    name={`descricao_cidade`}
                    control={control}
                    render={({ field }) => <TextEditor name={`descricao_cidade`} {...field} placeholder="Resposta" />}
                  />
                </Flex>

                <Text fontSize="23px" fontWeight="bold" textTransform="uppercase">
                  LOCALIZAÇÃO DE {data?.nome_cidade}
                </Text>
                <Box w="100%" height="500px" bg="gray.200" />
              </Flex>
            </Flex>

            <Flex flexDir={'column'} gap={5}>
              <Text fontSize="23px" fontWeight="bold">
                O que os nossos clientes dizem
              </Text>

              <Box w="100%" height="195px" bg="gray.200" />
            </Flex>

            <Flex flexDir={'column'} gap={5}>
              <Text fontSize="23px" fontWeight="bold">
                Depoimentos em Destaque
              </Text>

              <Grid gridTemplateColumns={'repeat(3, 1fr)'} gap={5}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <Box key={index} w="360px" h="220px" bg="gray.200" borderRadius="md" mx="auto" />
                ))}
              </Grid>

              <Box w="355px" height="48px" bg="gray.200" mx="auto" />
            </Flex>

            <ListaDuvidas nomeCidade={data.nome_cidade} />

            <Flex flexDir={'column'} gap={5}>
              <ListaTopicos nomeCidade={data?.nome_cidade} />
            </Flex>

            <Flex flexDir={'column'} gap={5}>
              <ListaPerguntasRespostas nomeCidade={data?.nome_cidade} />
            </Flex>

            <Flex flexDir={'column'} gap={5}>
              <Box position="relative" padding="10">
                <Divider orientation="horizontal" borderColor="gray.300" borderWidth="1px" borderStyle="solid" />

                <AbsoluteCenter bg="white" px="4">
                  Sobre
                </AbsoluteCenter>
              </Box>

              <Button
                onClick={() => gerarGptAsync({ cidade: data.nome_cidade, campo: 'sobre' })}
                colorScheme="blue"
                size="lg"
                bgColor="blue.500"
                isLoading={isLoadingGerarGpt}
                mr="auto"
              >
                Gerar Sobre (Descrição)
              </Button>

              <Text fontSize="23px" fontWeight="bold">
                Sobre - Título
              </Text>
              <Controller
                name={`sobre.titulo`}
                control={control}
                render={({ field }) => <TextEditor height="60px" name={`sobre.titulo`} {...field} placeholder="Resposta" />}
              />
            </Flex>

            <Flex flexDir={'column'} gap={5} pb={20}>
              <Text fontSize="23px" fontWeight="bold">
                Sobre - Descrição
              </Text>
              <Controller
                name={`sobre.descricao`}
                control={control}
                render={({ field }) => <TextEditor name={`sobre.descricao`} {...field} placeholder="Resposta" />}
              />
            </Flex>
          </Box>

          <Flex
            alignItems="center"
            gap={5}
            justify="center"
            w="100%"
            position="sticky"
            bottom="0"
            bg="white"
            p="10px"
            borderTopWidth="1px"
            borderTopColor="gray.300"
            zIndex="2"
          >
            <Flex as={Link} to="/adm/cidades" alignItems="center" gap={2} color="blue.500">
              <ArrowLeftIcon />
              <Text>Voltar</Text>
            </Flex>

            <Button type="submit" colorScheme="green" size="lg" isLoading={isLoadingUpdateArticle} isDisabled={!formState.isDirty}>
              Salvar
            </Button>

            {!isSuccess && (
              <Tooltip label="Para publicar a cidade, é necessário salvar as alterações" aria-label="A tooltip">
                <Button
                  colorScheme="blue"
                  size="lg"
                  isLoading={isLoadingPublicarCidade}
                  cursor="not-allowed"
                  bgColor="gray.300"
                  _hover={{ bg: 'gray.300' }}
                >
                  Publicar
                </Button>
              </Tooltip>
            )}

            {isSuccess && (
              <Button
                onClick={handlePublicarCidade}
                colorScheme="blue"
                size="lg"
                bgColor="blue.500"
                isLoading={isLoadingPublicarCidade}
                isDisabled={!formState.isDirty || !isSuccess}
              >
                Publicar
              </Button>
            )}
          </Flex>
        </Box>
      </FormProvider>
    </Box>
  )
}
